import { useEffect, useState } from "react";
import {
  Plus, Calendar, Clock, MapPin, Trash2, Sparkles, Loader2, Utensils, Plane, Bed, Compass, X,
  ArrowUp, ArrowDown, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import AppLayout, { PageHeader } from "@/components/app/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { travelApi } from "@/lib/travelApi";

const KIND_META = {
  activity:  { icon: Compass,  label: "Actividad",  color: "text-coral" },
  meal:      { icon: Utensils, label: "Comida",     color: "text-amber-600" },
  transport: { icon: Plane,    label: "Transporte", color: "text-sky-600" },
  lodging:   { icon: Bed,      label: "Hospedaje",  color: "text-emerald-600" },
  free:      { icon: Sparkles, label: "Libre",      color: "text-purple-600" },
};

function eachDay(startISO, endISO) {
  const out = [];
  const s = new Date(startISO); const e = new Date(endISO);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

// ══════════════════════════════════════════════════════════════════════════
// TripPlannerPanel — reusable panel (embed in Travel tab OR standalone /mis-planes)
// ══════════════════════════════════════════════════════════════════════════
export function TripPlannerPanel({ embedded = false }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [places, setPlaces] = useState([]);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    Promise.all([travelApi.listPlans(), travelApi.listPlaces({ limit: 60 })])
      .then(([p, pl]) => { setPlans(p); setPlaces(pl); })
      .catch(() => { /* noop */ })
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="border hairline bg-cream-card rounded-2xl p-8 text-center" data-testid="planner-signed-out">
        <Compass className="w-8 h-8 mx-auto text-coral mb-3" />
        <p className="font-serif text-xl mb-1">Planificá tu próximo viaje</p>
        <p className="text-sm text-ink-mid">Inicia sesión para crear cronogramas día por día con actividades, comidas, hospedaje y sugerencias de IA.</p>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : ""}>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-ink-mid">
          {loading ? "Cargando…" : `${plans.length} plan${plans.length === 1 ? "" : "es"}`}
        </p>
        <button
          data-testid="plan-new-btn"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 bg-coral text-white text-xs px-4 py-2 rounded-full hover:bg-coral/90 transition"
        >
          <Plus className="w-3.5 h-3.5" /> Nuevo plan
        </button>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="plans-grid">
        {!loading && plans.length === 0 && (
          <p className="col-span-full text-center text-sm text-ink-mid py-12" data-testid="plans-empty">
            Todavía no tenés planes. Creá el primero y armá tu cronograma.
          </p>
        )}
        {plans.map((p) => (
          <button
            key={p.id}
            data-testid={`plan-${p.id}`}
            onClick={() => setSelected(p)}
            className="text-left border hairline bg-cream-card rounded-2xl p-4 hover:border-coral hover:shadow-md transition"
          >
            <p className="font-mono text-[9px] uppercase tracking-widest text-coral">◊ {p.destination_name || "Sin destino"}</p>
            <h3 className="font-serif text-xl tracking-tight mt-1">{p.title}</h3>
            <p className="text-[12px] font-mono text-ink-mid mt-2 inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {p.start_date} → {p.end_date}
            </p>
            {p.notes && <p className="text-xs text-ink-mid mt-2 line-clamp-2">{p.notes}</p>}
          </button>
        ))}
      </section>

      {showCreate && (
        <CreatePlanModal
          places={places}
          onClose={() => setShowCreate(false)}
          onCreated={(p) => setPlans((arr) => [p, ...arr])}
        />
      )}
      {selected && (
        <PlanDetailModal
          plan={selected}
          onClose={() => setSelected(null)}
          onDeleted={(pid) => { setPlans((arr) => arr.filter((p) => p.id !== pid)); setSelected(null); }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Standalone /mis-planes route (kept for backwards compatibility + deep links)
// ══════════════════════════════════════════════════════════════════════════
export default function MyPlans() {
  return (
    <AppLayout hideRightRail>
      <PageHeader
        kicker="◊ Mis planes"
        title="Mis planes de viaje."
        subtitle="Organizá cada día por hora — actividades, comidas, hospedaje. Con IA opcional."
      />
      <TripPlannerPanel />
    </AppLayout>
  );
}

/* ─── Create plan modal ─── */
function CreatePlanModal({ places, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", destination_place_id: "", destination_name: "",
    start_date: "", end_date: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onPlace = (pid) => {
    const p = places.find((x) => x.id === pid);
    setForm((f) => ({
      ...f,
      destination_place_id: pid,
      destination_name: p ? `${p.name}, ${p.country}` : f.destination_name,
    }));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.start_date || !form.end_date) {
      toast.error("Título, fecha inicio y fin son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const p = await travelApi.createPlan({ ...form, destination_place_id: form.destination_place_id || null });
      toast.success("Plan creado");
      onCreated(p);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[9999] bg-ink/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <form onSubmit={submit} className="bg-cream w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl border hairline shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="p-5 border-b hairline flex justify-between items-center">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-coral">◊ Nuevo plan</p>
            <h3 className="font-serif text-2xl">Planeá tu viaje</h3>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full border hairline grid place-items-center"><X className="w-4 h-4" /></button>
        </header>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <Field label="Título *">
            <input data-testid="plan-create-title" value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Kioto sakura 2026" className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral" required />
          </Field>
          <Field label="Destino">
            <select data-testid="plan-create-place" value={form.destination_place_id} onChange={(e) => onPlace(e.target.value)} className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral">
              <option value="">— Elegí un destino —</option>
              {places.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.country}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inicio *">
              <input data-testid="plan-create-start" type="date" value={form.start_date} onChange={(e) => update("start_date", e.target.value)} className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral" required />
            </Field>
            <Field label="Fin *">
              <input data-testid="plan-create-end" type="date" value={form.end_date} onChange={(e) => update("end_date", e.target.value)} className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral" required />
            </Field>
          </div>
          <Field label="Notas">
            <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={3} className="w-full bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm outline-none focus:border-coral resize-none" />
          </Field>
        </div>
        <footer className="p-5 border-t hairline flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-4 py-2 rounded-full border hairline hover:bg-cream-card">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="plan-create-submit" className="text-xs px-5 py-2 rounded-full bg-ink text-cream hover:bg-coral transition disabled:opacity-50 inline-flex items-center gap-2">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Crear plan
          </button>
        </footer>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-mid mb-1">{label}</label>
      {children}
    </div>
  );
}

/* ─── Plan detail modal (timeline + items + reordering) ─── */
function PlanDetailModal({ plan, onClose, onDeleted }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addDay, setAddDay] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [editing, setEditing] = useState(null); // item being edited

  const reload = () => {
    let cancelled = false;
    setLoading(true);
    travelApi
      .listPlanItems(plan.id)
      .then((data) => { if (!cancelled) setItems(data); })
      .catch(() => { /* noop */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  };
  useEffect(() => reload(), [plan.id]);

  const days = eachDay(plan.start_date, plan.end_date);
  const itemsByDay = days.reduce((acc, d) => { acc[d] = items.filter((i) => i.day_date === d); return acc; }, {});

  const removeItem = async (iid) => {
    try {
      await travelApi.deletePlanItem(plan.id, iid);
      setItems((arr) => arr.filter((i) => i.id !== iid));
    } catch { toast.error("No se pudo borrar"); }
  };

  const moveItem = async (day, itemId, direction) => {
    const dayItems = [...itemsByDay[day]];
    const idx = dayItems.findIndex((it) => it.id === itemId);
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= dayItems.length) return;
    [dayItems[idx], dayItems[newIdx]] = [dayItems[newIdx], dayItems[idx]];
    // Optimistic update
    setItems((arr) => {
      const others = arr.filter((i) => i.day_date !== day);
      return [...others, ...dayItems.map((it, i) => ({ ...it, order_index: i }))];
    });
    try {
      await travelApi.reorderPlanItems(plan.id, day, dayItems.map((it) => it.id));
    } catch {
      toast.error("No se pudo reordenar");
      reload();
    }
  };

  const askAI = async () => {
    setAiLoading(true);
    try {
      const res = await travelApi.planAiSuggest(plan.id);
      const suggestions = res.suggestions || [];
      if (suggestions.length === 0) {
        toast.error("La IA no devolvió sugerencias.");
        return;
      }
      const created = [];
      for (const s of suggestions) {
        try {
          const it = await travelApi.createPlanItem(plan.id, {
            title: s.title || "Actividad",
            kind: (s.kind && ["activity","meal","transport","lodging","free"].includes(s.kind)) ? s.kind : "activity",
            day_date: s.day_date, time: s.time || null,
            duration_min: s.duration_min || null, location_name: s.location_name || "",
            notes: s.notes || "",
          });
          created.push(it);
        } catch { /* skip failed */ }
      }
      setItems((arr) => [...arr, ...created]);
      toast.success(`${created.length} items agregados por la IA`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "IA no disponible");
    } finally { setAiLoading(false); }
  };

  const deletePlan = async () => {
    if (!confirm("¿Eliminar este plan y todos sus items?")) return;
    try { await travelApi.deletePlan(plan.id); toast.success("Plan eliminado"); onDeleted(plan.id); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-ink/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="plan-detail-modal" onClick={onClose}>
      <div className="bg-cream w-full sm:max-w-3xl max-h-[95vh] rounded-t-3xl sm:rounded-3xl border hairline shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <header className="p-5 border-b hairline flex items-start justify-between shrink-0">
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-widest text-coral">◊ {plan.destination_name}</p>
            <h2 className="font-serif text-2xl sm:text-3xl leading-tight">{plan.title}</h2>
            <p className="text-[12px] font-mono text-ink-mid mt-1 inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {plan.start_date} → {plan.end_date} · {days.length} días · {items.length} items
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={deletePlan} data-testid="plan-delete" className="w-9 h-9 rounded-full border hairline grid place-items-center hover:bg-red-50 hover:text-red-700" aria-label="Eliminar plan"><Trash2 className="w-4 h-4" /></button>
            <button onClick={onClose} className="w-9 h-9 rounded-full border hairline grid place-items-center"><X className="w-4 h-4" /></button>
          </div>
        </header>

        <div className="px-5 py-3 border-b hairline bg-coral/5 flex items-center gap-2 shrink-0">
          <Sparkles className="w-4 h-4 text-coral" />
          <p className="text-xs text-ink flex-1">Ordená las actividades en el orden que las querés hacer — flechas ↑↓. La IA arma el itinerario base.</p>
          <button onClick={askAI} disabled={aiLoading} data-testid="plan-ai-suggest" className="text-xs bg-ink text-cream px-3 py-1.5 rounded-full hover:bg-coral transition disabled:opacity-50 inline-flex items-center gap-1.5">
            {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Sugerir con IA
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && <p className="text-center text-sm text-ink-mid">Cargando itinerario…</p>}
          {!loading && days.map((d, i) => (
            <section key={d} data-testid={`plan-day-${d}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-serif text-lg">
                  Día {i + 1} <span className="font-mono text-[10px] text-ink-mid tracking-widest uppercase ml-2">{d}</span>
                </h3>
                <button onClick={() => { setAddDay(d); setShowAdd(true); }} data-testid={`plan-day-${d}-add`} className="text-xs text-coral hover:underline inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Añadir item
                </button>
              </div>
              {itemsByDay[d].length === 0 ? (
                <p className="text-xs text-ink-mid py-3 border hairline border-dashed rounded-xl text-center">Sin items aún.</p>
              ) : (
                <ol className="space-y-2">
                  {itemsByDay[d].map((it, idx) => {
                    const meta = KIND_META[it.kind] || KIND_META.activity;
                    const Icon = meta.icon;
                    const first = idx === 0;
                    const last = idx === itemsByDay[d].length - 1;
                    return (
                      <li key={it.id} data-testid={`plan-item-${it.id}`} className="group flex gap-2 border hairline bg-cream-card rounded-xl p-3">
                        <div className="flex flex-col items-center gap-0.5 shrink-0 pt-1">
                          <button
                            onClick={() => moveItem(d, it.id, "up")}
                            disabled={first}
                            data-testid={`plan-item-${it.id}-up`}
                            className="w-6 h-6 rounded-full grid place-items-center text-ink-mid hover:text-coral hover:bg-cream disabled:opacity-20 disabled:cursor-not-allowed transition"
                            aria-label="Mover arriba"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <span className="font-mono text-[9px] text-ink-mid">{idx + 1}</span>
                          <button
                            onClick={() => moveItem(d, it.id, "down")}
                            disabled={last}
                            data-testid={`plan-item-${it.id}-down`}
                            className="w-6 h-6 rounded-full grid place-items-center text-ink-mid hover:text-coral hover:bg-cream disabled:opacity-20 disabled:cursor-not-allowed transition"
                            aria-label="Mover abajo"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </button>
                        </div>
                        <div className={`shrink-0 w-9 h-9 rounded-full bg-cream-deep grid place-items-center ${meta.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {it.time && <span className="font-mono text-[11px] text-coral inline-flex items-center gap-1"><Clock className="w-3 h-3" />{it.time}</span>}
                            <span className="font-mono text-[9px] uppercase tracking-widest text-ink-mid">{meta.label}</span>
                            {it.duration_min && <span className="font-mono text-[10px] text-ink-mid">· {it.duration_min}min</span>}
                            {it.cost_usd != null && <span className="font-mono text-[10px] text-emerald-700">· USD {it.cost_usd}</span>}
                          </div>
                          <p className="text-sm font-medium mt-0.5">{it.title}</p>
                          {it.location_name && <p className="text-[11px] text-ink-mid inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{it.location_name}</p>}
                          {it.notes && <p className="text-[12px] text-ink-mid mt-1">{it.notes}</p>}
                        </div>
                        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition shrink-0">
                          <button
                            onClick={() => setEditing(it)}
                            data-testid={`plan-item-${it.id}-edit`}
                            className="w-7 h-7 rounded-full grid place-items-center text-ink-mid hover:text-coral hover:bg-cream"
                            aria-label="Editar item"
                          >
                            <ChevronLeft className="w-3.5 h-3.5 rotate-180" />
                          </button>
                          <button
                            onClick={() => removeItem(it.id)}
                            data-testid={`plan-item-${it.id}-delete`}
                            className="w-7 h-7 rounded-full grid place-items-center text-ink-mid hover:text-coral hover:bg-cream"
                            aria-label="Borrar item"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          ))}
        </div>

        {showAdd && (
          <ItemFormModal
            planId={plan.id}
            days={days}
            defaultDay={addDay}
            onClose={() => setShowAdd(false)}
            onSaved={(it) => setItems((arr) => [...arr, it])}
          />
        )}
        {editing && (
          <ItemFormModal
            planId={plan.id}
            days={days}
            item={editing}
            onClose={() => setEditing(null)}
            onSaved={(updated) => setItems((arr) => arr.map((x) => (x.id === updated.id ? updated : x)))}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Add / Edit item modal (shared) ─── */
function ItemFormModal({ planId, days, item, defaultDay, onClose, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    title: item?.title || "",
    kind: item?.kind || "activity",
    day_date: item?.day_date || defaultDay || (days?.[0] ?? ""),
    time: item?.time || "",
    duration_min: item?.duration_min ?? "",
    location_name: item?.location_name || "",
    notes: item?.notes || "",
    cost_usd: item?.cost_usd ?? "",
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.day_date) { toast.error("Título y fecha requeridos"); return; }
    setSaving(true);
    const payload = {
      title: form.title,
      kind: form.kind,
      day_date: form.day_date,
      time: form.time || null,
      duration_min: form.duration_min !== "" ? Number(form.duration_min) : null,
      location_name: form.location_name,
      notes: form.notes,
      cost_usd: form.cost_usd !== "" ? Number(form.cost_usd) : null,
    };
    try {
      const saved = isEdit
        ? await travelApi.updatePlanItem(planId, item.id, payload)
        : await travelApi.createPlanItem(planId, payload);
      toast.success(isEdit ? "Item actualizado" : "Item añadido");
      onSaved(saved); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[10000] bg-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <form onSubmit={submit} className="bg-cream w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border hairline shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="p-5 border-b hairline flex justify-between items-center">
          <h3 className="font-serif text-xl">{isEdit ? "Editar item" : "Añadir al plan"}</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full border hairline grid place-items-center"><X className="w-4 h-4" /></button>
        </header>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <Field label="Título *">
            <input data-testid="item-title" value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Fushimi Inari al amanecer" className="w-full bg-cream-card border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral" required />
          </Field>
          <Field label="Tipo">
            <select data-testid="item-kind" value={form.kind} onChange={(e) => update("kind", e.target.value)} className="w-full bg-cream-card border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral">
              <option value="activity">Actividad</option>
              <option value="meal">Comida</option>
              <option value="transport">Transporte</option>
              <option value="lodging">Hospedaje</option>
              <option value="free">Libre</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha *">
              {days?.length > 0 ? (
                <select
                  data-testid="item-date"
                  value={form.day_date}
                  onChange={(e) => update("day_date", e.target.value)}
                  className="w-full bg-cream-card border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral"
                  required
                >
                  {days.map((d, i) => <option key={d} value={d}>Día {i + 1} · {d}</option>)}
                </select>
              ) : (
                <input data-testid="item-date" type="date" value={form.day_date} onChange={(e) => update("day_date", e.target.value)} className="w-full bg-cream-card border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral" required />
              )}
            </Field>
            <Field label="Hora">
              <input data-testid="item-time" type="time" value={form.time} onChange={(e) => update("time", e.target.value)} className="w-full bg-cream-card border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Duración (min)">
              <input value={form.duration_min} onChange={(e) => update("duration_min", e.target.value)} placeholder="60" type="number" min="0" className="w-full bg-cream-card border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral" />
            </Field>
            <Field label="Costo estimado (USD)">
              <input value={form.cost_usd} onChange={(e) => update("cost_usd", e.target.value)} placeholder="25" type="number" min="0" step="0.01" className="w-full bg-cream-card border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral" />
            </Field>
          </div>
          <Field label="Lugar">
            <input value={form.location_name} onChange={(e) => update("location_name", e.target.value)} placeholder="Fushimi Inari" className="w-full bg-cream-card border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral" />
          </Field>
          <Field label="Notas">
            <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} className="w-full bg-cream-card border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral resize-none" />
          </Field>
        </div>
        <footer className="p-5 border-t hairline flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-4 py-2 rounded-full border hairline hover:bg-cream-card">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="item-submit" className="text-xs px-5 py-2 rounded-full bg-ink text-cream hover:bg-coral transition disabled:opacity-50 inline-flex items-center gap-2">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {isEdit ? "Guardar" : "Añadir"}
          </button>
        </footer>
      </form>
    </div>
  );
}
